import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Box,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { calculateMinutesSince } from '../util';
import { tenXService } from '../../../../../services/TenXService';
import styles from './viewportList.module.css';


interface DeviceData {
  device_sn: string;
  cm_name?: string | null;
  last_image_timestamp: string;
}

interface DevicesResponse {
  [deviceName: string]: DeviceData;
}

interface DeviceRow {
  deviceName: string;
  cmName: string | null;
  lastUpdatedInMinutes: number;
}


interface ViewportListProps {
  onDeviceSelect: (deviceName: string) => void;
  selectedDevice: string | null;
}

const ViewportList: React.FC<ViewportListProps> = ({ onDeviceSelect, selectedDevice }) => {
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');

  const fetchDevices = async () => {
    setLoading(true);
    setError(null);

    try {
      const data: DevicesResponse = await tenXService.getDevices();

      const deviceArray: DeviceRow[] = Object.entries(data).map(
        ([deviceName, deviceData]) => ({
          deviceName,
          cmName: deviceData.cm_name ?? null,
          lastUpdatedInMinutes: calculateMinutesSince(deviceData.last_image_timestamp),
        })
      );

      setDevices(deviceArray);
      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch devices';
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Populate list on startup
  useEffect(() => {
    fetchDevices();
  }, []);

  const handleRefresh = () => {
    fetchDevices(); // re-fetch the device list
  };

  // Helper function to get chip color based on last updated time
  // Returns color and label for the status chip
  const getStatusChip = (minutes: number): { color: 'success' | 'warning' | 'error', label: string } => {
    if (minutes <= 30) {
      // updated within 30 minutes
      return { color: 'success', label: 'Active' };
    } else if (minutes <= 1440) {
      // updated within 24 hours (1440 minutes)
      return { color: 'warning', label: 'Recent' };
    } else {
      // updated more than 24 hours ago
      return { color: 'error', label: 'Stale' };
    }
  };

  const handleCopyToken = async () => {
    try {
      await tenXService.copyAuthToken();
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    setTimeout(() => setCopyStatus('idle'), 2000);
  };

  const handleRowClick = (deviceName: string) => {
    onDeviceSelect(deviceName); // update state for display
  };

  // Header actions stay rendered in every state so a failed load can be retried.
  return (
    <Box>
      <Box className={styles.RefreshBtn}>
        <Button
          variant="outlined"
          startIcon={<MenuBookIcon />}
          onClick={() => tenXService.openApiDocs()}
        >
          API Docs
        </Button>
        <Button
          variant="outlined"
          startIcon={copyStatus === 'copied' ? <CheckIcon /> : <ContentCopyIcon />}
          color={copyStatus === 'failed' ? 'error' : 'primary'}
          onClick={handleCopyToken}
        >
          {copyStatus === 'copied' ? 'Copied' : copyStatus === 'failed' ? 'Copy failed' : 'Copy JWT'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <CircularProgress />
        </div>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device Name</TableCell>
                <TableCell align="right">Time Since Last Upload (minutes)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => {
                const statusChip = getStatusChip(device.lastUpdatedInMinutes);
                const isSelected = device.deviceName === selectedDevice;

                return (
                  <TableRow
                    className={`${styles.ViewportList_Rows} ${isSelected ? styles.selected : ''}`}
                    key={device.deviceName}
                    onClick={() => handleRowClick(device.deviceName)}
                    hover
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <div>{device.deviceName}</div>
                      {device.cmName && <div>({device.cmName})</div>}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: '150px' }}>
                      <div>{device.lastUpdatedInMinutes.toFixed(1)}</div>
                      <div className={styles.StatusChipContainer}>
                        <div className={`${styles.StatusChip} ${styles[statusChip.color]}`}>
                          {statusChip.label}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ViewportList;
